import { ExternalQuestion, RespondentAnswerTypes } from '@sm/question-widgets/respondent-survey';
import { MultipleOpenEndedAnswerType, OpenEndedAnswerType } from '@sm/question-definitions';
import { QuestionAnswers } from '~app/pages/SurveyTaking/v2/types';
import { ExistingAnswers, SurveyQuestionFamily } from '~lib/generatedGqlTypes';

const mapOpenEndedAnswerValues = (
  responses: ExistingAnswers[],
  question: ExternalQuestion
): OpenEndedAnswerType['values'] | MultipleOpenEndedAnswerType['values'] => {
  return [];
};

const mapAnswerValues = (responses: ExistingAnswers[], question: ExternalQuestion): RespondentAnswerTypes['values'] => {
  const { family, variant } = question;

  switch (family as SurveyQuestionFamily) {
    case 'OPEN_ENDED': {
      return mapOpenEndedAnswerValues(responses, question);
    }
    default: {
      if (process.env.NODE_ENV === 'development') {
        /**
         * This is a longging that would only happen during development. It is not expected to happen in production as it will be removed by the compiler.
         */
        console.debug(`Question type not mapped: ${family}, ${variant}`);
      }
      return [];
    }
  }
};

/**
 * Converts server responses to application state answers.
 *
 * @param {Partial<RespApiAnswerInput>[]} responses - The list of responses from the server.
 * @param {ExternalQuestion[]} questions - The list of questions on the current page.
 * @returns {QuestionAnswers} - The mapped answers in the application state format.
 */
export const mapResponseToSurveyAnswers = (
  responses: ExistingAnswers[],
  questions: ExternalQuestion[]
): QuestionAnswers => {
  return questions.reduce<QuestionAnswers>((acc, question) => {
    const backendResponses = responses.filter(response => response.questionId === question.id);

    // no need to map if there are no responses
    if (backendResponses.length === 0) {
      return acc;
    }
    const values = mapAnswerValues(backendResponses, question);
    acc[question.id] = {
      questionId: question.id,
      values,
      isDirty: false,
      touched: false,
      family: question.family,
      variant: question.variant,
    };

    return acc;
  }, {});
};

export default mapResponseToSurveyAnswers;
